import { c as _c } from "react/compiler-runtime";
// `<PageSeo>` — single-line SEO injection for any page that owns a
// shareable URL.
//
// Replaces the 8-line boilerplate (resolve origin, compute pageUrl,
// compute ogImageUrl, build ctx, build jsonLd, render JsonLdScript) with
// one element. Adding SEO to a new page is now:
//
// ```tsx
// import { PageSeo } from '@/lib/seo';
//
// export default async function Page({ params }): Promise<JSX.Element> {
//   const { id } = await params;
//   return (
//     <>
//       <PageSeo
//         surface={{ kind: 'market', marketPubkey: id }}
//         path={`/markets/${id}`}
//         name={market.title}
//         description={market.summary}
//       />
//       <Content />
//     </>
//   );
// }
// ```
//
// Notes:
//   - Must render inside a Server Component (page.tsx, layout.tsx) so
//     the JSON-LD appears in the initial HTML crawlers fetch.
//   - `path` MUST NOT include a locale prefix — the site uses
//     `localePrefix: 'never'` so canonical URLs never contain `/en/...`.
//   - Pass `name`/`description` only when you already have them. Don't
//     add new SDK fetches just to enrich the JSON-LD; the per-surface
//     builders fall back to sensible generics when those are absent.

import type { JSX } from 'react';
import type { ShareSurface } from '@/lib/share/events';
import { getOgImageUrl } from '@/lib/share/og/url';
import { buildSurfaceJsonLd, JsonLdScript } from './json-ld';
import { buildCanonicalUrl } from './origin';
export interface PageSeoProps {
  /** Discriminated share surface for this page. */
  surface: ShareSurface;
  /** Canonical path relative to origin, e.g. `/markets/abc123`.
   *  Locale prefix is never included. */
  path: string;
  demoMode?: boolean;
  /** Optional human-readable name (market title, user display name).
   *  Lets the JSON-LD builder produce a richer payload. */
  name?: string;
  /** Optional description override. */
  description?: string;
  /** ISO-8601. Surface creation timestamp (market opened, gift sent). */
  dateCreated?: string;
  /** ISO-8601. Last meaningful update (most recent trade, settlement). */
  dateModified?: string;
  /** Whole-USDC volume — drives Schema.org `interactionStatistic`. */
  volumeUsdc?: number;
  /** Distinct traders / participants. */
  traderCount?: number;
  /** ISO-8601. Resolution / expiry endpoint for event-shaped surfaces. */
  endDate?: string;
  /** Resolved-market winning answer ("Yes", "Trump") — drives Q&A rich results. */
  resolvedAnswer?: string;
  /** DOM id for the script tag — defaults to `${surface.kind}-jsonld`. */
  id?: string;
}
export function PageSeo(props) {
  const $ = _c(7);
  let id;
  let surface;
  let t0;
  if ($[0] !== props) {
    const {
      surface: t1,
      path,
      id: t2,
      demoMode,
      ...ctxFields
    } = props;
    surface = t1;
    id = t2;
    const pageUrl = buildCanonicalUrl(path);
    const ogImageUrl = getOgImageUrl(surface, {
      demoMode: demoMode === true
    });
    t0 = buildSurfaceJsonLd(surface, {
      pageUrl,
      ogImageUrl,
      ...Object.fromEntries(Object.entries(ctxFields).filter(_temp))
    });
    $[0] = props;
    $[1] = id;
    $[2] = surface;
    $[3] = t0;
  } else {
    id = $[1];
    surface = $[2];
    t0 = $[3];
  }
  const jsonLd = t0;
  const t1 = id ?? `${surface.kind}-jsonld`;
  let t2;
  if ($[4] !== jsonLd || $[5] !== t1) {
    t2 = <JsonLdScript data={jsonLd} id={t1} />;
    $[4] = jsonLd;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function _temp(t0) {
  const [, v] = t0;
  return v !== undefined;
}